import { useCallback } from "react"

import {
  transport,
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import type { GetMetricHealthStatsResponse } from "@/types_api/backend/transport/get_metric_health_stats_response"
import type { ReviewOverviewProjectResource } from "@/types_api/backend/transport/get_review_overview_response"
import type {
  ReviewProjectMemberResource,
  Role as ReviewProjectMemberRole,
} from "@/types_api/backend/transport/list_review_project_members_response"
import type { ReviewProjectResource } from "@/types_api/backend/transport/update_review_project_response"
import type { HumanReviewProject } from "@/types/api/HumanReviewProject"

import { assertHumanReviewMetricLimit } from "../utils/humanReviewMetricLimit"
import {
  invalidateReviewAnnotationQueries,
  invalidateReviewOverviewCompletionQueries,
  invalidateReviewOverviewQueries,
} from "../utils/reviewOverviewCache"

export {
  mapAnnotationsWithMetricOutputsResponse,
  useAllAnnotationsByOrgTransport,
  useAnnotationsWithMetricOutputsTransport,
  useFeedbackForSimulationOutputTransport,
} from "./reviewAnnotationQueriesTransport"
export {
  useCreateReviewAnnotationTransport,
  useDeleteReviewAnnotationTransport,
  useUpdateReviewAnnotationTransport,
} from "./reviewAnnotationMutationsTransport"
export {
  mapReviewDisagreementStateResponse,
  useReviewDisagreementStateTransport,
} from "./reviewDisagreementStateTransport"
export type { ReviewDisagreementProjectState } from "./reviewDisagreementStateTransport"

type NonEmptyAssignees = [string, ...string[]]
interface ProjectWithBlindLabelingShownMetricIds {
  blind_labeling_shown_metric_ids?: string[] | null
}

export function toNonEmptyAssignees(
  assignees: string[] | undefined,
): NonEmptyAssignees | undefined {
  if (assignees === undefined) {
    return undefined
  }

  if (assignees.length === 0) {
    throw new Error("Human review projects must have at least one assignee.")
  }

  return [assignees[0], ...assignees.slice(1)]
}

function getBlindLabelingShownMetricIds(project: object): string[] | null {
  return (
    (project as ProjectWithBlindLabelingShownMetricIds)
      .blind_labeling_shown_metric_ids ?? null
  )
}

export function mapReviewOverviewProjectToHumanReviewProject(
  project: ReviewOverviewProjectResource,
): HumanReviewProject {
  return {
    ulid: project.ulid,
    name: project.name,
    description: project.description ?? null,
    organization_id: project.organization_id,
    created_at: project.created_at,
    last_updated_at: project.last_updated_at,
    assignees: project.assignees,
    linked_simulation_ids: project.linked_simulation_ids,
    linked_metric_ids: project.linked_metric_ids,
    notifications: project.notifications,
    opted_out_assignees: project.opted_out_assignees ?? [],
    project_type: project.project_type,
    project_rules: project.project_rules ?? null,
    blind_labeling_shown_metric_ids: getBlindLabelingShownMetricIds(project),
    review_label_options: project.review_label_options ?? null,
    review_label_input_mode: project.review_label_input_mode ?? null,
    review_label_selection_mode: project.review_label_selection_mode ?? null,
  }
}

export function mapReviewProjectResourceToHumanReviewProject(
  project: ReviewProjectResource,
): HumanReviewProject {
  const projectType = project.project_type.replace(/^PROJECT_/, "")

  return {
    ulid: project.id,
    name: project.display_name,
    description: project.description ?? null,
    organization_id: "",
    created_at: project.create_time,
    last_updated_at: project.update_time,
    assignees: project.assignees,
    linked_simulation_ids: project.linked_simulation_ids,
    linked_metric_ids: project.linked_metric_ids,
    notifications: project.notifications,
    opted_out_assignees: [],
    project_type:
      projectType === "COLLABORATIVE" ? "COLLABORATIVE" : "INDIVIDUAL",
    project_rules: project.project_rules ?? null,
    blind_labeling_shown_metric_ids: getBlindLabelingShownMetricIds(project),
    review_label_options: project.review_label_options ?? null,
    review_label_input_mode: project.review_label_input_mode ?? null,
    review_label_selection_mode: project.review_label_selection_mode ?? null,
    enforced_collaboration: project.enforced_collaboration ?? false,
  }
}

export function useHumanReviewProjectsTransport({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useTransportQuery({
    operation: transport.operations.getReviewOverview,
    version: 2,
    request: {
      include_linked_simulation_ids: false,
      include_simulation_completions: false,
      include_simulation_metadata: false,
    },
    options: {
      enabled,
      select: (data) =>
        data.projects.map(mapReviewOverviewProjectToHumanReviewProject),
    },
  })
}

export function useReviewProjectMembershipLogsTransport({
  projectId,
  enabled = true,
}: {
  projectId: string
  enabled?: boolean
}) {
  return useTransportQuery({
    operation: transport.operations.listReviewProjectMembershipLogs,
    request: { project_id: projectId },
    options: { enabled: !!projectId && enabled },
  })
}

export function useHumanReviewProjectWithSimulationIdsFetcherTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    async (projectId: string): Promise<HumanReviewProject> => {
      const response = await transportQueryUtils.fetch(
        transport.operationVersions.getReviewOverview.v2,
        {
          project_ids: [projectId],
          include_linked_simulation_ids: true,
          include_simulation_completions: false,
          include_simulation_metadata: false,
        },
      )
      const project = response.projects.find((row) => row.ulid === projectId)
      if (!project) {
        throw new Error("Project not found")
      }

      return mapReviewOverviewProjectToHumanReviewProject(project)
    },
    [transportQueryUtils],
  )
}

export function useMetricHealthStatsTransport({
  metricId,
  includeOldVersions = false,
  criteriaViewMode = "score",
  enabled = true,
}: {
  metricId?: string
  includeOldVersions?: boolean
  criteriaViewMode?: "score" | "criteria"
  enabled?: boolean
}) {
  return useTransportQuery({
    operation: transport.operations.getMetricHealthStats,
    request: {
      metric_id: metricId ?? "",
      include_old_versions: includeOldVersions,
      criteria_view_mode: criteriaViewMode,
    },
    options: {
      enabled: !!metricId && enabled,
      select: (response: GetMetricHealthStatsResponse) => ({
        conversations: response.conversations,
        annotations: response.annotations,
        agreementRate: response.agreement_rate,
        agreements: response.agreements,
        disagreements: response.disagreements,
        criteriaAgreement: response.criteria_agreement ?? null,
        humanAgreement: {
          humanAgreementRate: response.human_agreement.human_agreement_rate,
          humanAgreementTotal: response.human_agreement.human_agreement_total,
          humanAgreements: response.human_agreement.human_agreements,
          humanDisagreements: response.human_agreement.human_disagreements,
        },
      }),
    },
  })
}

export function useReviewMetricTransportInvalidation() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(() => {
    void transportQueryUtils.invalidateOperation(
      transport.operations.getAnnotationsWithMetricOutputs,
    )
    void transportQueryUtils.invalidateOperation(
      transport.operations.getMetricHealthStats,
    )
    void transportQueryUtils.invalidateOperation(
      transport.operations.getMetricAgreementHistory,
    )
  }, [transportQueryUtils])
}

export function useReviewAnnotationTransportInvalidation() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  // Annotation saves do not invalidate the org-wide overview — refetching every project after each
  // save is what this path exists to avoid. The completion-carrying queries are refreshed, though:
  // progress now reads server-computed maps, and no client patch can recompute "any assignee
  // finished this conversation" from one reviewer's edit.
  return useCallback(async () => {
    void transportQueryUtils.invalidateCacheTags([
      transport.cacheTags["review-annotations"],
    ])
    await Promise.all([
      invalidateReviewAnnotationQueries(queryClient),
      invalidateReviewOverviewCompletionQueries(queryClient),
    ])
  }, [queryClient, transportQueryUtils])
}

export function useHumanReviewProjectMutationsTransport() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  const invalidateReviewProjectQueries = useCallback(() => {
    void invalidateReviewOverviewQueries(queryClient)
    void invalidateHomeQueries(queryClient)
    void transportQueryUtils.invalidate(transport.resources["review-projects"])
    void transportQueryUtils.invalidate(
      transport.resources["review-annotations"],
    )
  }, [queryClient, transportQueryUtils])

  const createMutation = useTransportMutation(
    transport.operations.createReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )
  const updateMutation = useTransportMutation(
    transport.operations.updateReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )
  const deleteMutation = useTransportMutation(
    transport.operations.deleteReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )
  const duplicateMutation = useTransportMutation(
    transport.operations.duplicateReviewProject,
    {
      onSuccess: invalidateReviewProjectQueries,
    },
  )

  return {
    createProject: async (request: {
      name: string
      description: string | null
      assignees: [string, ...string[]]
      simulationOutputs?: string[]
      metrics?: string[]
      notifications?: boolean
      projectType?: "COLLABORATIVE" | "INDIVIDUAL"
      projectRules?: string[]
      reviewLabelOptions?: string[] | null
      reviewLabelInputMode?:
        | "OPTION_ONLY"
        | "CUSTOM_ONLY"
        | "OPTION_OR_CUSTOM"
        | null
      reviewLabelSelectionMode?: "SINGLE" | "MULTIPLE" | null
      enforcedCollaboration?: boolean
    }) => {
      const linkedSimulationIds = request.simulationOutputs ?? []
      const linkedMetricIds = request.metrics ?? []

      assertHumanReviewMetricLimit(request.metrics)

      return mapReviewProjectResourceToHumanReviewProject(
        (
          await createMutation.mutateAsync({
            display_name: request.name,
            description: request.description,
            assignees: request.assignees,
            linked_simulation_ids: linkedSimulationIds,
            linked_metric_ids: linkedMetricIds,
            notifications: request.notifications,
            project_type: request.projectType,
            project_rules: request.projectRules,
            review_label_options: request.reviewLabelOptions,
            review_label_input_mode: request.reviewLabelInputMode,
            review_label_selection_mode: request.reviewLabelSelectionMode,
            ...(request.enforcedCollaboration !== undefined
              ? { enforced_collaboration: request.enforcedCollaboration }
              : {}),
          })
        ).review_project,
      )
    },
    updateProject: async (request: {
      id: string
      name?: string
      description?: string | null
      assignees?: string[]
      simulationOutputs?: string[]
      metrics?: string[]
      notifications?: boolean
      projectType?: "COLLABORATIVE" | "INDIVIDUAL"
      projectRules?: string[]
      blindLabelingShownMetricIds?: string[]
      optedOutAssignees?: string[]
      reviewLabelOptions?: string[] | null
      reviewLabelInputMode?:
        | "OPTION_ONLY"
        | "CUSTOM_ONLY"
        | "OPTION_OR_CUSTOM"
        | null
      reviewLabelSelectionMode?: "SINGLE" | "MULTIPLE" | null
      enforcedCollaboration?: boolean
      metricAdditionCompletionAction?: "KEEP_COMPLETED" | "REOPEN_COMPLETED"
    }) => {
      assertHumanReviewMetricLimit(request.metrics)
      return mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            display_name: request.name,
            description: request.description,
            assignees: toNonEmptyAssignees(request.assignees),
            linked_simulation_ids: request.simulationOutputs,
            linked_metric_ids: request.metrics,
            notifications: request.notifications,
            project_type: request.projectType,
            project_rules: request.projectRules,
            ...(request.blindLabelingShownMetricIds !== undefined
              ? {
                  blind_labeling_shown_metric_ids:
                    request.blindLabelingShownMetricIds,
                }
              : {}),
            opted_out_assignees: request.optedOutAssignees,
            review_label_options: request.reviewLabelOptions,
            review_label_input_mode: request.reviewLabelInputMode,
            review_label_selection_mode: request.reviewLabelSelectionMode,
            ...(request.enforcedCollaboration !== undefined
              ? { enforced_collaboration: request.enforcedCollaboration }
              : {}),
            ...(request.metricAdditionCompletionAction !== undefined
              ? {
                  metric_addition_completion_action:
                    request.metricAdditionCompletionAction,
                }
              : {}),
          })
        ).review_project,
      )
    },
    // Exposed as dedicated actions because the backend forbids combining
    // add_linked_simulation_ids / remove_linked_simulation_ids with any
    // non-delta update field. Deltas also avoid the race where two
    // overlapping edits each send a stale "kept" array and stomp each other.
    addSimulationOutputs: async (request: {
      id: string
      simulationOutputs: string[]
    }) =>
      mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            add_linked_simulation_ids: request.simulationOutputs,
          })
        ).review_project,
      ),
    removeSimulationOutputs: async (request: {
      id: string
      simulationOutputs: string[]
    }) =>
      mapReviewProjectResourceToHumanReviewProject(
        (
          await updateMutation.mutateAsync({
            project_id: request.id,
            remove_linked_simulation_ids: request.simulationOutputs,
          })
        ).review_project,
      ),
    deleteProject: (projectId: string) =>
      deleteMutation.mutateAsync({ project_id: projectId }),
    duplicateProject: async (projectId: string) =>
      mapReviewProjectResourceToHumanReviewProject(
        (await duplicateMutation.mutateAsync({ project_id: projectId }))
          .review_project,
      ),
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDuplicating: duplicateMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    duplicateError: duplicateMutation.error,
  }
}

export type { ReviewProjectMemberRole }

// Effective per-project role rows (MANAGER-wins deduped) for a project. Assignees
// without an explicit row are absent here and default to ANNOTATOR in the UI.
export function useReviewProjectMembersTransport(
  projectId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useTransportQuery({
    operation: transport.operations.listReviewProjectMembers,
    request: { project_id: projectId ?? "" },
    options: {
      enabled: enabled && Boolean(projectId),
      select: (data) => data.members,
    },
  })
}

export function useReviewProjectMemberMutationsTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  const setRoleMutation = useTransportMutation(
    transport.operations.setReviewProjectMemberRole,
    {
      onSuccess: () => {
        void transportQueryUtils.invalidate(
          transport.resources["review-project-members"],
        )
      },
    },
  )

  return {
    setMemberRole: async (request: {
      projectId: string
      assignee: string
      role: ReviewProjectMemberRole
    }): Promise<ReviewProjectMemberResource> =>
      (
        await setRoleMutation.mutateAsync({
          project_id: request.projectId,
          assignee: request.assignee,
          role: request.role,
        })
      ).member,
    isSettingRole: setRoleMutation.isPending,
    setRoleError: setRoleMutation.error,
  }
}
